import { Button } from "@chakra-ui/button";
import { Box } from "@chakra-ui/layout";
import { Menu, MenuButton, MenuItem, MenuList } from "@chakra-ui/menu";
import { Flex } from "@chakra-ui/react";
import Container from "components/Container";
import ExportMenu from "components/ExportMenu";
import ModalFormConta from "components/Features/Financeiro/FormConta/ModalFormConta";
import { useLoading } from "components/GlobalLoading";
import DateRangePicker from "components/Input/DateRangePicker";
import ModalHistoricoConta from "components/ModalHistoryConta";
import NewTable from "components/NewTable";
import PageHeader from "components/Page/PageHeader";
import { useTenantInfo } from "core/features/relatorios/hooks/useTenantInfo";
import { getPeriodoRelatorio } from "core/features/relatorios/prestacao-contas/util/getPeriodoRelatorio";
import { OperacaoEnum } from "core/resources/enums/conta";
import { PermissaoEnum } from "core/resources/enums/permissao";
import colors from "core/resources/theme/colors";
import { useEstornarConta } from "features/conta/hooks";
import useMovimentacoesBancarias from "features/movimentacoes-bancarias/hooks/useMovimentacoes";
import { MovimentacaoBancariaItem } from "features/movimentacoes-bancarias/typings";
import { showConfirm2, showSuccess } from "infra/helpers/alerta";
import useDownloadRenderedPdf from "infra/helpers/useDownloadRenderedPdf";
import { useWindowSize } from "infra/helpers/window";
import { useCallback, useState } from "react";
import { BiLineChart, BiLineChartDown } from "react-icons/bi";
import { MdArrowDropDown, MdCheck, MdEdit, MdPreview } from "react-icons/md";
import ListingPageHeaderBar from "ui/components/Page/ListingPageHeaderBar";
import PDFRelatorioMovimentacaoFinanceira from "ui/components/RelatorioMovimentacaoFinanceira/components/PDFRelatorio";
import useMovimentacoesBancariasColumns from "../../hooks/useMovimentacoesBancariasColumns";
import DashboardMovimentacoes from "../DashboardMovimentacoes";
import { ModalContentConta } from "../ModalContentConta";
import ModalTransferenciaEntreContas from "../ModalTransferenciaEntreContas";
import useFiltroMovimentacoesBancarias from "./../../hooks/useFiltroMovimentacoesBancarias";
import { exportExcelMovimentacoesBancaria } from "./../../util/helper";
import ModalFiltrosMovimentacaoBancaria from "./../ModalFiltrosMovimentacaoBancaria";
import TagStatusFiltroMovimentacoes from "./../TagStatusFiltroMovimentacoes";

function ContentMovimentacoes() {
  const { filtro, onChangeFiltro, onResetFiltro, setFiltro } =
    useFiltroMovimentacoesBancarias();
  const [detalheId, setDetalheId] = useState<string | undefined>();
  const [detalheTransferenciaId, setDetalheTransferenciaId] = useState<
    string | undefined
  >();
  const [isOpenModalFiltro, setIsOpenModalFiltro] = useState(false);

  const [detalhesCadastroConta, setDetalhesCadastroConta] = useState<{
    operacao: OperacaoEnum;
  } | null>(null);

  const [detalhesEditarConta, setDetalhesEditarConta] = useState<{
    operacao: OperacaoEnum;
    id: string;
  } | null>();

  const [historyId, setHistoryId] = useState<string>();

  const columns = useMovimentacoesBancariasColumns();

  const {
    data: movimentacao,
    isLoading,
    isFetching,
    refetch,
  } = useMovimentacoesBancarias({
    ...filtro,
  });

  // const { mutate: handleDeleteConta, isLoading: isLoadingDelete } =
  //   useDeleteConta();

  const { mutate: estornarConta, isLoading: isLoadingEstornarConta } =
    useEstornarConta();

  const { isFetching: isFetchingInfo, refetch: refetchInfo } =
    useTenantInfo(false);

  const { onDownload: onExportPdf } = useDownloadRenderedPdf({
    fileName: `Relatório de Movimentações Bancárias.pdf`,
    Component: PDFRelatorioMovimentacaoFinanceira,
    loading: isFetching,
  });

  const _onExportPdf = useCallback(async () => {
    const { data: tenantInfo } = await refetchInfo();

    if (tenantInfo && movimentacao) {
      onExportPdf({
        ...tenantInfo,
        ...movimentacao,
        dataSaldo: filtro.dataFim,
        periodo: getPeriodoRelatorio(filtro.dataInicio, filtro.dataFim),
      });
    }
  }, [
    filtro.dataFim,
    filtro.dataInicio,
    movimentacao,
    onExportPdf,
    refetchInfo,
  ]);

  const _onExportExcel = useCallback(async () => {
    if (!movimentacao) return;

    exportExcelMovimentacoesBancaria({
      movimentacao,
      filtro,
    });
  }, [filtro, movimentacao]);

  const items = movimentacao ? movimentacao.items : [];

  const _isLoading = isLoading || isFetching;

  useLoading(isFetchingInfo || isLoadingEstornarConta);

  const windowSize = useWindowSize();

  const isMobile = windowSize.width <= 580;

  const numeroFiltroSelecionados =
    (filtro.pessoaId?.length ? 1 : 0) +
    (filtro.unidadeId?.length ? 1 : 0) +
    (filtro.contasBancariasIds?.length ? 1 : 0) +
    (filtro.centrosDeCustoIds?.length ? 1 : 0) +
    (filtro.categoriasFinanceirasIds?.length ? 1 : 0);

  // const handleDelete = async (ids: string[]) => {
  //   const { isConfirmed } = await showConfirm();

  //   if (isConfirmed) {
  //     handleDeleteConta(ids, {
  //       onSuccess: async () => {
  //         await showSuccess();

  //         refetch();
  //       },
  //     });
  //   }
  // };

  const onEstornarConta = async (id: string) => {
    estornarConta(id, {
      onSuccess: async () => {
        await showSuccess();

        refetch();
      },
    });
  };

  return (
    <>
      <Container>
        <PageHeader
          mb={isMobile ? "100px" : "0px"}
          title="Movimentações bancárias"
          midComponent={
            <Flex mt={isMobile ? "4px" : undefined} gap={2} alignItems="center">
              <DateRangePicker
                allowEmptyDates={{
                  dataInicio: true,
                  dataFim: false,
                }}
                onChange={(dataInicio, dataFim) => {
                  onChangeFiltro({
                    dataInicio,
                    dataFim,
                  });
                }}
                value={{
                  dataInicio: filtro.dataInicio,
                  dataFim: filtro.dataFim,
                }}
              />

              <Box pos="relative">
                <Menu>
                  <MenuButton
                    permissions={[
                      PermissaoEnum.Backoffice.Financeiro.ContasPagar.Criar,
                      PermissaoEnum.Backoffice.Financeiro.ContasReceber.Criar,
                    ]}
                    as={Button}
                    bg="white"
                    rightIcon={<MdArrowDropDown />}
                    borderWidth={1}
                    fontSize="14px"
                    height="38.5px"
                    _hover={{
                      bg: "white",
                      boxShadow: `0 0 0 2px ${colors.primary}`,
                    }}
                    _active={{
                      bg: "white",
                      boxShadow: `0 0 0 2px ${colors.primary}`,
                    }}
                  >
                    Nova movimentação
                  </MenuButton>
                  <MenuList>
                    <MenuItem
                      onClick={() => {
                        setDetalhesCadastroConta({
                          operacao: OperacaoEnum.Receita,
                        });
                      }}
                      icon={<BiLineChart fontSize={18} />}
                    >
                      Receita
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        setDetalhesCadastroConta({
                          operacao: OperacaoEnum.Despesa,
                        });
                      }}
                      icon={<BiLineChartDown fontSize={18} />}
                    >
                      Despesa
                    </MenuItem>
                  </MenuList>
                </Menu>
              </Box>
            </Flex>
          }
        />

        <Box mt={10}>
          <DashboardMovimentacoes
            isLoading={_isLoading}
            totais={movimentacao?.totais}
            dataFim={filtro.dataFim}
          />

          <Box>
            <Box>
              <ListingPageHeaderBar
                valueSearch={filtro.search || ""}
                onSearch={(v) => onChangeFiltro({ search: v })}
                visibleFiltro
                showOrderByButton
                badge
                badgeValue={numeroFiltroSelecionados}
                onFiltro={() => setIsOpenModalFiltro(true)}
                children={
                  <Flex
                    alignItems="center"
                    w="100%"
                    justifyContent="space-between"
                  >
                    <TagStatusFiltroMovimentacoes
                      filtro={filtro}
                      onChangeFiltro={onChangeFiltro}
                    />

                    {!_isLoading ? (
                      <ExportMenu
                        onExportPdf={_onExportPdf}
                        onExportarXlsx={_onExportExcel}
                      />
                    ) : null}
                  </Flex>
                }
                orderByOptions={[
                  {
                    value: "dataMovimentacao",
                    label: "Data de crédito",
                  },
                  {
                    value: "descricao",
                    label: "Descrição",
                  },
                  {
                    value: "Devedor",
                    label: "Devedor",
                  },
                  {
                    value: "valor",
                    label: "Valor",
                  },
                ]}
                orderBy={filtro.orderBy}
                onOrderByChange={(orderBy) => {
                  setFiltro((state) => ({
                    ...state,
                    orderBy,
                  }));
                }}
                orderDirection={filtro.orderDirection}
                onOrderDirectionChange={(orderDirection) => {
                  setFiltro((state) => ({
                    ...state,
                    orderDirection,
                  }));
                }}
              />
            </Box>
            <Box mt={4}>
              <NewTable<MovimentacaoBancariaItem>
                loading={_isLoading}
                columns={columns}
                dataSource={items}
                onRow={(record) => ({
                  onClick: () =>
                    record.transferenciaId
                      ? setDetalheTransferenciaId(record.transferenciaId)
                      : setDetalheId(record.id),
                  style: {
                    cursor: "pointer",
                  },
                })}
                actions={[
                  {
                    title: "Editar",
                    permission: (v) =>
                      v.operacao === OperacaoEnum.Despesa
                        ? PermissaoEnum.Backoffice.Financeiro.ContasPagar.Editar
                        : PermissaoEnum.Backoffice.Financeiro.ContasReceber
                            .Editar,
                    icon: () => <MdEdit />,
                    onClick: (v) =>
                      setDetalhesEditarConta({
                        id: v.id,
                        operacao: v.operacao,
                      }),
                  },
                  {
                    title: "Cancelar Liquidação",
                    icon: () => <MdCheck />,
                    onClick: (v) => {
                      showConfirm2({
                        message: "Deseja realmente estornar esta conta?",
                        onOk: () => onEstornarConta(v.id),
                      });
                    },
                  },
                  {
                    title: "Ver histórico",
                    icon: () => <MdPreview />,
                    permission: (v) =>
                      v.operacao === OperacaoEnum.Despesa
                        ? PermissaoEnum.Backoffice.Financeiro.ContasPagar
                            .Visualizar
                        : PermissaoEnum.Backoffice.Financeiro.ContasReceber
                            .Visualizar,
                    onClick: (v) => {
                      setHistoryId(v.id);
                    },
                  },
                  // {
                  //   title: "Excluir",
                  //   icon: () => <MdDelete />,
                  //   permission: (v) =>
                  //     v.operacao === OperacaoEnum.Despesa
                  //       ? PermissaoEnum.Backoffice.Financeiro.ContasPagar
                  //           .Excluir
                  //       : PermissaoEnum.Backoffice.Financeiro.ContasReceber
                  //           .Excluir,
                  //   onClick: (v) => handleDelete([v.id]),
                  // },
                ]}
              />
            </Box>
          </Box>
        </Box>

        <ModalTransferenciaEntreContas
          openModal={!!detalheTransferenciaId}
          onClose={() => setDetalheTransferenciaId(undefined)}
          transferenciaId={detalheTransferenciaId}
        />

        <ModalContentConta
          isOpenModal={!!detalheId}
          onClose={() => setDetalheId(undefined)}
          contaId={detalheId}
        />

        {isOpenModalFiltro ? (
          <ModalFiltrosMovimentacaoBancaria
            onClose={() => setIsOpenModalFiltro(false)}
            visible={isOpenModalFiltro}
            filtro={filtro}
            onChangeFiltro={onChangeFiltro}
            onSave={() => {
              refetch();
              setIsOpenModalFiltro(false);
            }}
            onReset={onResetFiltro}
          />
        ) : null}

        {detalhesCadastroConta ? (
          <ModalFormConta
            operacao={detalhesCadastroConta.operacao}
            isOpen={!!detalhesCadastroConta}
            onClose={() => {
              setDetalhesCadastroConta(null);
            }}
            onSuccess={() => {
              refetch();
              setDetalhesCadastroConta(null);
            }}
            onSuccessImmediatelyAfterSave={false}
            requiredLiquidar
          />
        ) : null}

        {detalhesEditarConta ? (
          <ModalFormConta
            id={detalhesEditarConta.id}
            operacao={detalhesEditarConta.operacao}
            isOpen={!!detalhesEditarConta}
            showRegisterAnother={false}
            onClose={() => {
              setDetalhesEditarConta(null);
            }}
            onSuccess={() => {
              refetch();
              setDetalhesEditarConta(null);
            }}
            onSuccessImmediatelyAfterSave={false}
          />
        ) : null}

        {historyId ? (
          <ModalHistoricoConta
            isOpenModal={!!historyId}
            onClose={() => {
              setHistoryId(undefined);
            }}
            contaId={historyId}
          />
        ) : null}
      </Container>
    </>
  );
}

export default ContentMovimentacoes;
